/** Вкладка «Контроль — к плану производства» — порт renderControlMatrix (стр. 5693-5779).
 * FIFO-очередь взятых под наблюдение артикулов; метрики каждой позиции считаются в срезе
 * ЕЁ МП (порт controlQueueRows с подменой activeTab); позиции без данных синтезируются
 * из сохранённых полей. Quick-plan закрывает позицию атомарно (исправленный баг). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import React, { useMemo } from "react";
import { toast } from "sonner";

import type { MpCode, OrdersMatrixReport } from "@/api/orders";
import * as planningApi from "@/api/orders-planning";
import { extractErrorMessage } from "@/lib/api-errors";
import { cn } from "@/lib/utils";
import { fmtDate, int0, signed } from "./format";
import {
  buildView,
  recommendation,
  rowMetrics,
  stockDayRange,
  type RowMetrics,
  type ViewRow,
} from "./metrics";
import { displayValue, productionWeekIndex } from "./production";
import { QuickPlanWidget } from "./QuickPlanWidget";
import { Sparkline } from "./Sparkline";
import { useOrdersEdit } from "./useOrdersEdit";

const MP_LABELS: Record<MpCode, string> = { wb: "Wildberries", ozon: "Ozon", ym: "Яндекс Маркет" };

const REC_CLS: Record<string, string> = {
  red: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
  yellow: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
  green: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400",
  blue: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-400",
};

export function ControlTab({ report }: { report: OrdersMatrixReport }): React.ReactElement {
  const qc = useQueryClient();
  const canEdit = useOrdersEdit();
  const controlQ = useQuery({ queryKey: ["orders", "control"], queryFn: planningApi.listControl });
  const channelsQ = useQuery({
    queryKey: ["orders", "refs", "channels"],
    queryFn: planningApi.listChannels,
  });
  const year = new Date().getFullYear();
  const prodQ = useQuery({
    queryKey: ["orders", "production", year],
    queryFn: () => planningApi.getProduction(year),
  });

  const views = useMemo(() => {
    const map = new Map<MpCode, { rows: ViewRow[]; ctx: ReturnType<typeof buildView>["ctx"] }>();
    for (const mp of ["wb", "ozon", "ym"] as MpCode[]) {
      map.set(mp, buildView(report, mp, ""));
    }
    return map;
  }, [report]);

  const removeMut = useMutation({
    mutationFn: (v: { mp: string; article: string }) =>
      planningApi.removeControl({ mp: v.mp, article: v.article, reason: "ok" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders", "control"] });
      toast.success("Позиция контроля закрыта");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось закрыть позицию")),
  });

  const productionSummary = (article: string): { weeks: string[]; total: number } => {
    if (!prodQ.data) return { weeks: [], total: 0 };
    const byWeek = new Map<string, planningApi.ProductionCell[]>();
    for (const c of prodQ.data.cells) {
      if (c.article !== article) continue;
      const list = byWeek.get(c.week_key);
      if (list) list.push(c);
      else byWeek.set(c.week_key, [c]);
    }
    const produced = new Set(
      prodQ.data.produced.filter((p) => p.article === article).map((p) => p.week_key),
    );
    const weeks: string[] = [];
    let total = 0;
    for (const [weekKey, cells] of [...byWeek.entries()].sort(
      (a, b) => productionWeekIndex(a[0]) - productionWeekIndex(b[0]),
    )) {
      if (produced.has(weekKey)) continue;
      const qty = displayValue(cells);
      if (qty > 0) {
        weeks.push(`Н${productionWeekIndex(weekKey)} · ${int0(qty)}`);
        total += qty;
      }
    }
    return { weeks, total };
  };

  if (controlQ.isLoading) {
    return (
      <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-4 py-6 text-sm text-muted-foreground">
        <RefreshCw className="size-4 animate-spin" /> Загружаю очередь контроля…
      </div>
    );
  }

  const items = controlQ.data ?? [];
  if (!items.length) {
    return (
      <div className="rounded-md border bg-muted/30 px-4 py-6 text-sm text-muted-foreground">
        Очередь пуста. Отметьте чекбокс в колонке «В контроль» на вкладках WB, Ozon или Яндекс
        Маркет — позиция появится здесь.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium">Контроль — к плану производства · {items.length}</div>
      <div className="max-h-[calc(100vh-260px)] overflow-auto rounded-md border">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10 bg-background">
            <tr className="border-b text-muted-foreground">
              {[
                "Артикул / название", "МП", "Добавил", "ABC/XYZ", "Действие",
                "Тек. мес", "Пред. мес", "Δ", "Прош. MTD", "Δ MTD", "30 дней",
                "Ост. МП", "Ост. 1С", "Совокупно", "Запас, дн",
                "План пр-ва", "Посл. продажа", "Тренд", "В производстве", "",
              ].map((h, i) => (
                <th key={i} className={cn("px-2 py-1.5 font-medium", i < 5 || [15, 17, 18].includes(i) ? "text-left" : "text-right")}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const view = views.get(item.mp);
              const row = view?.rows.find((r) => r.article === item.article);
              const m: RowMetrics | null = row && view ? rowMetrics(row, view.ctx) : null;
              const rec = row && m ? recommendation(row, m) : null;
              const range = m ? stockDayRange(m.stockDays) : null;
              const prod = productionSummary(item.article);
              return (
                <tr key={`${item.mp}::${item.article}`} className="border-b last:border-0">
                  <td className="max-w-56 px-2 py-1.5">
                    <div className="truncate font-medium">{item.article}</div>
                    <div className="truncate text-[10px] text-muted-foreground">
                      {row?.name ?? item.name}
                      {item.brand && ` · ${item.brand}`}
                      {item.code_1c && ` · ${item.code_1c}`}
                    </div>
                  </td>
                  <td className="px-2 py-1.5">{MP_LABELS[item.mp]}</td>
                  <td className="px-2 py-1.5">
                    <div>{item.created_by || "—"}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {new Date(item.created_at).toLocaleString("ru-RU")}
                    </div>
                  </td>
                  <td className="px-2 py-1.5">{row?.abcxyz ?? "—"}</td>
                  <td className="px-2 py-1.5">
                    {rec ? (
                      <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-medium", REC_CLS[rec.cls])} title={rec.note}>
                        {rec.title}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">нет данных</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{m ? int0(m.current) : "—"}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{m ? int0(m.previous) : "—"}</td>
                  <td className={cn("px-2 py-1.5 text-right tabular-nums", m && (m.delta >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-500 dark:text-red-400"))}>
                    {m ? signed(m.delta) : "—"}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{m ? int0(m.previousMtd) : "—"}</td>
                  <td className={cn("px-2 py-1.5 text-right tabular-nums", m && (m.deltaMtd >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-500 dark:text-red-400"))}>
                    {m ? signed(m.deltaMtd) : "—"}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{m ? int0(m.last30) : "—"}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{row ? int0(row.stockMp) : "—"}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{row ? int0(row.stock1c) : "—"}</td>
                  <td className="px-2 py-1.5 text-right font-medium tabular-nums">
                    {row ? int0(row.stockTotal) : "—"}
                  </td>
                  <td
                    className="px-2 py-1.5 text-right font-medium tabular-nums"
                    style={range ? { backgroundColor: `${range.color}22` } : undefined}
                  >
                    {m ? (m.stockDays === null ? "—" : int0(m.stockDays)) : "—"}
                  </td>
                  <td className="px-2 py-1.5">
                    <QuickPlanWidget
                      article={item.article}
                      channels={channelsQ.data ?? []}
                      defaultChannel={item.mp}
                      closeControlMp={item.mp}
                      disabled={!canEdit}
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right text-muted-foreground">
                    {fmtDate(row?.lastSale ?? null)}
                  </td>
                  <td className="px-2 py-1.5">{row && <Sparkline buckets={row.spark} />}</td>
                  <td className="max-w-40 px-2 py-1.5">
                    {prod.weeks.length ? (
                      <details>
                        <summary className="cursor-pointer text-[10px]">
                          {prod.weeks.length} нед. / {int0(prod.total)} шт
                        </summary>
                        <div className="mt-0.5 flex flex-wrap gap-1">
                          {prod.weeks.map((w) => (
                            <span key={w} className="rounded bg-muted px-1 py-0.5 text-[9px]">{w}</span>
                          ))}
                        </div>
                      </details>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">нет плана</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    <button
                      type="button"
                      disabled={!canEdit}
                      className="rounded border px-2 py-0.5 text-[10px] font-medium hover:bg-emerald-50 disabled:opacity-50 dark:hover:bg-emerald-950"
                      title="Контроль отработан"
                      onClick={() => removeMut.mutate({ mp: item.mp, article: item.article })}
                    >
                      OK
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
